import type { DriverCatalog } from '@casa/driver-host';
import { manifestSecretKeys } from '@casa/driver-sdk';
import { placeholderName, tilesOf, type Dashboard } from '@casa/model';

/** The credential fields of each driver, the installed ones' included, from their manifests (secrets SE-3). */
export function secretKeysOfDrivers(drivers: DriverCatalog) : (driver: string) => readonly string[] {
    return (driver) => {
        const resolved = drivers.resolve(driver);

        return resolved.ok ? manifestSecretKeys(resolved.manifest) : [];
    };
}

/**
 * The credentials in a dashboard, as values to mask in the log (decision §12): each connection's
 * config fields that its driver's manifest marks secret, and the camera URLs with their stream keys
 * (the last part of the path, which is the key on the owner's cameras).
 */
export function dashboardSecrets(dashboard: Dashboard, drivers: DriverCatalog) : string[] {
    const secrets: string[] = [];
    for (const connection of Object.values(dashboard.connections)) {
        const driver = drivers.resolve(connection.driver);
        if (!driver.ok) {
            continue;
        }
        for (const key of manifestSecretKeys(driver.manifest)) {
            const value = connection.config[key];
            // A placeholder (secrets SE-1) is no credential: nothing to mask.
            if (typeof value === 'string' && value !== '' && placeholderName(value) === undefined) {
                secrets.push(value);
            }
        }
    }

    for (const screen of dashboard.screens) {
        for (const tile of tilesOf(screen)) {
            // A tile with a source has no address of its own (video-hls.md VH-2).
            if (tile.type !== 'video' || !('url' in tile.options)) {
                continue;
            }

            secrets.push(tile.options.url);
            const key = tile.options.url.split(/[?#]/)[0]?.split('/').at(-1);
            if (key) {
                secrets.push(key);
            }
        }
    }

    return secrets;
}
